import {
    ExportDateRangePreset,
    ExportDateRangeValue,
    ExportField,
    ExportFieldValue,
    ExportFlowCapability,
    ExportStep,
} from '../types';

/**
 * Hulpjes voor de exportwizard (/export/v2). Platformkennis hoort hier niet:
 * de wizard kent alleen staptypes, veldtypes en acties.
 */

export const EXPORT_FLOW_VERSION = '1';

// Alleen wat de wizard echt kan. Zonder step.pick.browse (geen mappen bladeren)
// en zonder action.sso_relogin (de SSO-login is vanuit de wizard niet te herstarten).
export const EXPORT_FLOW_CAPABILITIES: readonly ExportFlowCapability[] = [
    'step.form', 'step.pick', 'step.confirm', 'step.message', 'step.web',
    'field.text', 'field.date_range', 'field.select', 'field.toggle',
    'action.open_url', 'action.web',
];

const STEP_KINDS: readonly string[] = ['form', 'pick', 'confirm', 'message', 'web'];
const FIELD_TYPES: readonly string[] = ['text', 'date_range', 'select', 'toggle'];

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isKnownField = (field: unknown): field is ExportField =>
    isObject(field) && typeof field.key === 'string' && typeof field.type === 'string' && FIELD_TYPES.includes(field.type)
    && (field.type !== 'select' || Array.isArray(field.options))
    && (field.type !== 'date_range' || isDateRange(field.default));

const allKnown = (fields: unknown): boolean => Array.isArray(fields) && fields.every(isKnownField);

/**
 * Kan de wizard deze stap zelf tonen? Een onbekend staptype of veldtype laat
 * hij nooit stilletjes weg: dan volgt de webstap.
 */
export const canRenderStep = (step: unknown): step is ExportStep => {
    if (!isObject(step) || typeof step.kind !== 'string' || !STEP_KINDS.includes(step.kind)) return false;
    switch (step.kind) {
        case 'form':
            return allKnown(step.fields);
        case 'confirm':
            return step.fields === undefined || allKnown(step.fields);
        case 'pick': {
            const search = step.search;
            if (search !== null && search !== undefined && !(isObject(search) && allKnown(search.fields))) return false;
            const create = step.create;
            if (create !== null && create !== undefined && !(isObject(create) && isKnownField(create.field))) return false;
            return step.results === null || step.results === undefined || Array.isArray(step.results);
        }
        default:
            return true;
    }
};

/** Links opent de wizard alleen als ze met https:// beginnen. */
export const isHttpsUrl = (value: unknown): value is string => {
    if (typeof value !== 'string' || !value.toLowerCase().startsWith('https://')) return false;
    try {
        return new URL(value).protocol === 'https:';
    } catch {
        return false;
    }
};

export const openHttpsUrl = (url: unknown): void => {
    if (isHttpsUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
};

// --- Datums (YYYY-MM-DD, lokale tijd) ---

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isDateRange = (value: unknown): value is ExportDateRangeValue =>
    isObject(value) && typeof value.start === 'string' && typeof value.end === 'string';

export const toIsoDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

const parseIsoDate = (value: string): Date | null => {
    if (!DATE_RE.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

/** Aantal dagen van start tot eind, zoals de server het telt (eind - start). */
const daysBetween = (start: Date, end: Date): number =>
    Math.round((Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
        - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86400000);

/** Vandaag; deze week (maandag t/m zondag); deze maand. */
export const datePresetRange = (preset: ExportDateRangePreset, today: Date = new Date()): ExportDateRangeValue => {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (preset === 'week') {
        const monday = new Date(day);
        monday.setDate(day.getDate() - ((day.getDay() + 6) % 7));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { start: toIsoDate(monday), end: toIsoDate(sunday) };
    }
    if (preset === 'month') {
        return {
            start: toIsoDate(new Date(day.getFullYear(), day.getMonth(), 1)),
            end: toIsoDate(new Date(day.getFullYear(), day.getMonth() + 1, 0)),
        };
    }
    return { start: toIsoDate(day), end: toIsoDate(day) };
};

export const presetFits = (range: ExportDateRangeValue, maxDays?: number): boolean => {
    if (maxDays === undefined) return true;
    const start = parseIsoDate(range.start);
    const end = parseIsoDate(range.end);
    return Boolean(start && end && daysBetween(start, end) <= maxDays);
};

// --- Veldwaarden ---

export const initialFieldValue = (field: ExportField): ExportFieldValue => {
    switch (field.type) {
        case 'text':
            return typeof field.default === 'string' ? field.default : '';
        case 'date_range':
            return { start: field.default.start, end: field.default.end };
        case 'select':
            return typeof field.default === 'string' ? field.default : '';
        case 'toggle':
            return field.default === true;
    }
};

export const initialFieldValues = (fields: ExportField[]): Record<string, ExportFieldValue> =>
    Object.fromEntries(fields.map((field) => [field.key, initialFieldValue(field)]));

export type FieldProblem =
    | { kind: 'required' }
    | { kind: 'minLength'; count: number }
    | { kind: 'maxLength'; count: number }
    | { kind: 'dateInvalid' }
    | { kind: 'dateOrder' }
    | { kind: 'maxDays'; count: number };

/**
 * Dezelfde regels als de server, zodat de knop pas werkt als de invoer klopt.
 * De server blijft de baas: zijn validatiefout komt bij het veld te staan.
 */
export const fieldProblem = (field: ExportField, value: ExportFieldValue | undefined): FieldProblem | null => {
    switch (field.type) {
        case 'text': {
            const text = typeof value === 'string' ? value.trim() : '';
            if (!text) return field.required === false ? null : { kind: 'required' };
            if (field.min_length && text.length < field.min_length) return { kind: 'minLength', count: field.min_length };
            if (field.max_length && text.length > field.max_length) return { kind: 'maxLength', count: field.max_length };
            return null;
        }
        case 'date_range': {
            if (!isDateRange(value)) return { kind: 'dateInvalid' };
            const start = parseIsoDate(value.start);
            const end = parseIsoDate(value.end);
            if (!start || !end) return { kind: 'dateInvalid' };
            if (end < start) return { kind: 'dateOrder' };
            if (field.max_days !== undefined && daysBetween(start, end) > field.max_days) {
                return { kind: 'maxDays', count: field.max_days };
            }
            return null;
        }
        case 'select': {
            if (typeof value !== 'string' || value === '') return field.required === false ? null : { kind: 'required' };
            return field.options.some((option) => option.value === value) ? null : { kind: 'required' };
        }
        case 'toggle':
            return null;
    }
};

/** De waarden zoals de server ze verwacht: tekst getrimd, lege optionele velden weg. */
export const fieldInputs = (fields: ExportField[], values: Record<string, ExportFieldValue>): Record<string, ExportFieldValue> => {
    const inputs: Record<string, ExportFieldValue> = {};
    fields.forEach((field) => {
        const value = values[field.key] ?? initialFieldValue(field);
        if (field.type === 'text') {
            const text = typeof value === 'string' ? value.trim() : '';
            if (text) inputs[field.key] = text;
        } else if (field.type === 'select') {
            if (typeof value === 'string' && value !== '') inputs[field.key] = value;
        } else {
            inputs[field.key] = value;
        }
    });
    return inputs;
};

// --- Versturen ---

/** Een nieuwe sleutel per verzending; dezelfde sleutel verstuurt nooit twee keer. */
export const newIdempotencyKey = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
